import { createSlice } from '@reduxjs/toolkit';
import { t } from 'i18next';
import { toast } from 'react-toastify';
import { ResponseApi } from '../BaseApi';
import { postSessionStorageLoader } from '../../Apis/functions';

const EMPTY_LAST_SEARCH_TOTAL = { option: '', totalElements: 0, fromAdvancedSearch: false };

/**
 * Stores the amount of records returned by the last executed search together with the model
 * it belongs to, so the export button can warn the user before exporting a large file.
 *
 * @param {Object} state - Current slice state.
 * @param {Object} action - Fulfilled action of the search request.
 * @param {boolean} [fromAdvancedSearch=false] - Whether the total comes from an advanced search
 *        (filter based) instead of a simple one. The export button only warns for the former.
 * @returns {void}
 */
const setLastSearchTotal = (state, action, fromAdvancedSearch = false) => {
    const { option, body } = action?.meta?.arg || {};
    state.lastSearchTotal = {
        option: `${option ?? body?.model ?? ''}`,
        totalElements: action?.payload?.data?.data?.totalElements ?? 0,
        fromAdvancedSearch,
    };
};

const SearchSlice = createSlice({
    name: 'search',
    initialState: {
        getAttributesFilter: [],
        getConditionFilter: [],
        getValuesByOptionAndField: [],
        getValuesByOptionFieldAndUserId: [],
        searchByFilters: [],
        searchByFiltersCustom: [],
        getListSearchModels: [],
        getValuesToFilterLogByField: [],
        searchCrudLogByModel: [],
        simpleSearch: [],
        simpleSearchWithId: [],
        lastSearchTotal: EMPTY_LAST_SEARCH_TOTAL,
    },
    reducers: {
        clearSearchFilters: (state, action) => {
            state.searchByFilters = [];
            state.searchByFiltersCustom = [];
            state.searchCrudLogByModel = [];
            state.lastSearchTotal = EMPTY_LAST_SEARCH_TOTAL;
        },
        resetSearchCrudLogByModel: (state, action) => {
            state.searchCrudLogByModel = [];
        },
    },
    extraReducers: {
        'search/getAttributesFilter/fulfilled': (state, action) => {
            state.getAttributesFilter = ResponseApi(action.payload);
        },
        'search/getConditionFilter/fulfilled': (state, action) => {
            state.getConditionFilter = ResponseApi(action.payload);
        },
        'search/getValuesByOptionAndField/fulfilled': (state, action) => {
            state.getValuesByOptionAndField = ResponseApi(action.payload);
        },
        'search/getValuesByOptionFieldAndUserId/fulfilled': (state, action) => {
            state.getValuesByOptionFieldAndUserId = ResponseApi(action.payload);
        },
        'invitation/getInvitationValuesByOptionFieldAndUserId/fulfilled': (state, action) => {
            state.getValuesByOptionFieldAndUserId = ResponseApi(action.payload);
        },
        'search/searchByFilters/fulfilled': (state, action) => {
            const { data, status } = action.payload || {};
            if (status >= 200 && status < 300) {
                state.searchByFilters = data?.data;
                setLastSearchTotal(state, action, true);
                postSessionStorageLoader(false);
            } else if (status >= 400 && status < 600) {
                postSessionStorageLoader(false);
                toast.error(t('error_when_searching').toUpperCase());
            }
        },
        'search/searchByFiltersCustom/fulfilled': (state, action) => {
            const { data, status } = action.payload || {};
            if (status >= 200 && status < 300) {
                state.searchByFiltersCustom = data?.data;
                setLastSearchTotal(state, action, true);
                postSessionStorageLoader(false);
            } else if (status >= 400 && status < 600) {
                postSessionStorageLoader(false);
                toast.error(t('error_when_searching').toUpperCase());
            }
        },
        'search/getListSearchModels/fulfilled': (state, action) => {
            state.getListSearchModels = ResponseApi(action.payload);
        },
        'search/getValuesToFilterLogByField/fulfilled': (state, action) => {
            state.getValuesToFilterLogByField = ResponseApi(action.payload);
        },
        'search/searchCrudLogByModel/fulfilled': (state, action) => {
            const { data, status } = action.payload || {};
            if (status >= 200 && status < 300) {
                state.searchCrudLogByModel = data?.data;
                postSessionStorageLoader(false);
            } else if (status >= 400 && status < 600) {
                postSessionStorageLoader(false);
                toast.error(t('error_when_searching').toUpperCase());
            }
        },
        'search/SimpleSearch/fulfilled': (state, action) => {
            const { data, status } = action.payload || {};
            if (status >= 200 && status < 300) {
                state.simpleSearch = data?.data;
                setLastSearchTotal(state, action);
                postSessionStorageLoader(false);
            } else if (status >= 400 && status < 600) {
                postSessionStorageLoader(false);
            }
        },
        'search/SimpleSearchWithId/fulfilled': (state, action) => {
            const { data, status } = action.payload || {};
            if (status >= 200 && status < 300) {
                state.simpleSearchWithId = data?.data;
                setLastSearchTotal(state, action);
                postSessionStorageLoader(false);
            } else if (status >= 400 && status < 600) {
                postSessionStorageLoader(false);
                toast.error(t('error_when_searching').toUpperCase());
            }
        },
        clearSearchFilters: (state) => {
            state.searchByFilters = [];
            state.searchByFiltersCustom = [];
            state.searchCrudLogByModel = [];
            state.simpleSearch = [];
            state.lastSearchTotal = EMPTY_LAST_SEARCH_TOTAL;
        },
    },
});

export const { clearSearchFilters, resetSearchCrudLogByModel } = SearchSlice.actions;

export default SearchSlice.reducer;
